import React, { useEffect, useMemo, useState } from 'react';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import { SlColumn, SlRow } from '@stackline/ui';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import { useTableData, UseTableDataProps } from 'src/serverProxy/useTableData';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { useAppSelector } from 'src/utils/Hooks';
import ProductGrid, { GRID_ITEMS_PER_PAGE } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import { getAppName } from 'src/utils/app';
import { MetricListContainerField } from 'src/components/BeaconV2Components/MetricList/MetricListContainer';
import { useLatestCumulativeReviewWeekId } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewsHooks';

export const FETCH_CHUNK_SIZE = 100;

export interface ReviewsV2ProductGridBySalesProps {
  indexName: string;
  /**
   * How the response will be sorted
   */
  sortFieldName: string;
  mainFieldName?: string;
  fields: MetricListContainerField[];
  showHeader?: boolean;
  useTableDataProps?: Partial<Omit<UseTableDataProps, 'fields' | 'indexName'>>;
  groupBy?: string;
  iconList?: IconsList[];
  onChangePage?: (args: {
    page: number;
    /**
     * Page sent to advanced search, not the one displayed.
     * For example, we could fetch 100 items at at time but only display 20
     * in the grid, so if the page is 6, the adjusted page would be 2.
     *
     * It will be calculated as Math.ceil(page / PAGES_PER_CHUNK)
     */
    chunkedPage: number;
  }) => void;
  selectedTableOption: any;
  hideComparison?: boolean;
}

/**
 * Product grid that is sorted by sales
 */
const ReviewsV2ProductGridBySales = ({
  indexName,
  sortFieldName,
  mainFieldName,
  fields,
  showHeader = true,
  useTableDataProps,
  groupBy = 'stacklineSku',
  iconList = [IconsList.export, IconsList.tile, IconsList.table],
  onChangePage,
  selectedTableOption,
  hideComparison = false
}: ReviewsV2ProductGridBySalesProps) => {
  const PAGES_PER_CHUNK = useMemo(() => FETCH_CHUNK_SIZE / GRID_ITEMS_PER_PAGE, []);
  const { startWeek, endWeek } = useAppSelector((state) => state.mainTimePeriod);
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const [page, setPage] = React.useState(1);
  const { latestCumulativeWeekId } = useLatestCumulativeReviewWeekId();
  const [dataByPageNumber, setDataByPageNumber] = useState({} as { [key: number]: any[] });

  const iconsList = useMemo(() => {
    return iconList.filter((icon) => icon !== IconsList.tile);
  }, [iconList]);

  const { isLoading: salesLoading, data: salesRows } = useTableData<{ metadata: any }>({
    fields: [{ name: 'retailSales' }],
    groupByFieldName: 'stacklineSku',
    indexName: BEACON_INDEX_NAMES.SALES,
    sortFieldName: 'retailSales',
    requestId: `paginatedTableData-${BEACON_INDEX_NAMES.SALES}-stacklineSku`,
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: Math.ceil(page / PAGES_PER_CHUNK),
    ...useTableDataProps
  });

  const productsBySales = useMemo(
    () =>
      salesRows?.reduce((acc, datum) => {
        const stacklineSku = datum?.metadata?.stacklineSku;
        const retailSales = datum?.retailSales_sum_value || 0;
        if (stacklineSku) {
          acc[stacklineSku] = { stacklineSku, retailSales };
        }
        return acc;
      }, {}),
    [salesRows]
  );

  const productSkus = useMemo(() => Object.keys(productsBySales), [productsBySales]);

  const {
    isLoading: tableRowsLoading,
    getValuesByFieldName,
    getValueDifferencesByFieldName,
    data: tableRows
  } = useTableData<{ metadata: any }>({
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !salesLoading
    },
    fields: [...fields],
    groupByFieldName: groupBy,
    indexName,
    requestId: 'product-grid',
    itemsPerPage: FETCH_CHUNK_SIZE,
    pageNumber: 1,
    sortFieldName,
    ...(hideComparison
      ? {
          customComparisonStartWeekId: startWeek,
          customComparisonEndWeekId: endWeek
        }
      : {}),
    buildRequest: (builder) => {
      builder.addConditionTermFilter('stacklineSku', 'should', productSkus);

      // if it is cumulative, use latestCumulativeWeekId for min and max weekId
      if (selectedTableOption.includes('lifetime')) {
        builder.modifyAggregation((aggBuilder) =>
          aggBuilder
            .clearConditionRangeFilters()
            .addConditionRangeFilter('weekId', latestCumulativeWeekId, latestCumulativeWeekId)
            .clearComparisonRangeFilters()
            .addComparisonRangeFilter('weekId', comparisonEndWeek, comparisonEndWeek)
        );
      }

      return builder;
    },
    ...useTableDataProps
  });

  const tableData = useMemo(() => {
    const fieldValuesMap = fields.reduce((acc, { name }) => {
      return {
        ...acc,
        [name]: {
          values: getValuesByFieldName(name),
          differences: getValueDifferencesByFieldName(name)
        }
      };
    }, {} as { values: number[]; differences: number[] });

    return tableRows.map((row, index) => ({
      ...row.metadata,
      retailSales: productsBySales[row?.metadata?.stacklineSku]?.retailSales || 0,
      ...fields.reduce((acc, { name }) => {
        return {
          ...acc,
          [name]: {
            value: fieldValuesMap[name].values[index],
            difference: hideComparison ? 0 : fieldValuesMap[name].differences[index]
          }
        };
      }, {} as { [key: string]: number }),
      responseRow: row
    }));
  }, [fields, getValueDifferencesByFieldName, getValuesByFieldName, tableRows, productsBySales, hideComparison]);

  // Hide the pagination controls if we have less than the max amount of grid items per page on the first page of results.
  // This does not handle the edge case where we could have the exact amount of grid items per page.
  const hidePagination =
    (tableData || []).length < GRID_ITEMS_PER_PAGE && page === 1 && !tableRowsLoading && !salesLoading;

  /**
   * Updates the full data set so that we can sort
   * by increasing or decreasing
   */
  useEffect(() => {
    setDataByPageNumber((oldData) => {
      const chunkedPage = Math.ceil(page / PAGES_PER_CHUNK);

      return {
        ...oldData,
        [chunkedPage]: tableData
      };
    });
  }, [tableData, page, PAGES_PER_CHUNK]);

  /**
   * The full table data, sorted by the sort option.
   */
  const fullTableData = useMemo(() => {
    const allTableData = Object.entries(dataByPageNumber).reduce((accumulator, [_, d]) => {
      accumulator = [...accumulator, ...d];
      return accumulator;
    }, []);

    const sortedBySales = allTableData.sort((a, b) => b.retailSales - a.retailSales);
    return sortedBySales;
  }, [dataByPageNumber]);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getAppName(), indexName, mainFieldName || sortFieldName),
    [indexName, mainFieldName, sortFieldName]
  );

  /**
   * Returns the page number we are using to fetch. For example, if we show 20 items
   * per page and fetch 100 items, then page 1-5 will be 1, page 6-10 will be 2, etc.
   */
  const computeChunkedPage = (displayedPage: number) => {
    return Math.ceil(displayedPage / PAGES_PER_CHUNK);
  };

  const handleNextPage = () => {
    const adjustedPage = ((page - 1) % PAGES_PER_CHUNK) + 1;
    const startIndex = (adjustedPage - 1) * GRID_ITEMS_PER_PAGE;
    const endIndex = startIndex + GRID_ITEMS_PER_PAGE;

    if (tableRows.slice(startIndex, endIndex).length < GRID_ITEMS_PER_PAGE) {
      return;
    }

    const newPage = page + 1;
    setPage(newPage);

    if (onChangePage) {
      onChangePage({
        page: newPage,
        chunkedPage: computeChunkedPage(newPage)
      });
    }
  };

  // go to the first page when sortOption changes or the time period options change
  useEffect(() => {
    setPage(1);
  }, [selectedTableOption, startWeek, endWeek, comparisonStartWeek, comparisonEndWeek]);

  const handlePrevPage = () => {
    const newPage = Math.max(1, page - 1);
    setPage(newPage);

    if (onChangePage) {
      onChangePage({
        page: newPage,
        chunkedPage: computeChunkedPage(newPage)
      });
    }
  };

  return (
    <SlColumn widths="full" spacing="lg">
      {showHeader ? (
        <EntityTableHeader
          enableSwitchingLayouts
          defaultView="tile"
          handleChangeLayout={() => {}}
          iconsList={iconsList}
          paddingTop="0px"
          paddingBottom="0px"
        />
      ) : null}

      <ProductGrid
        data={fullTableData}
        loading={tableRowsLoading || salesLoading}
        metricType={field.metricType}
        page={page}
        fieldName={mainFieldName || sortFieldName}
        secondaryMetricOverride={hideComparison ? () => '' : undefined}
        showDirectionOverride={hideComparison ? () => false : undefined}
      />

      <div style={{ marginTop: '20px' }}>
        {hidePagination ? null : (
          <SlRow horizontalPosition="end">
            <Pagination
              currentPage={page}
              shouldUseInfinitePagination
              onClickNext={() => handleNextPage()}
              onClickPrevious={() => handlePrevPage()}
            />
          </SlRow>
        )}
      </div>
    </SlColumn>
  );
};

export default ReviewsV2ProductGridBySales;
